import { push } from "connected-react-router";
import { actionCreatorAsync, AsyncAction } from "./actionHelpers";
import * as api from "../api/assessment";
import { IThunkDispatch } from "../store";
import { pageSize as limit } from "../containers/Assessment/AssessmentList";
import { Assessment } from "../interfaces/AssessmentInterfaces";
import { notificationAdd } from "./notification";
import { createErrorMessage, MODULE, updateErrorMessage } from "../helpers/messages";
import { ASSESSMENT_SEARCH_BY } from "../containers/Assessment/AssessmentListSearch";
import {
  addAssessmentTemplateImageToRC,
  removeAssessmentTemplateImageFromRC,
  setAssessmentTemplateImagesInRC
} from "./resourceCentre";
import { AttachmentInterface } from "../interfaces/AttachmentInterface";

export enum Type {
  GET_ASSESSMENTS = "UPDATE_BOOKING",
  GET_ASSESSMENT_BY_ID = "GET_ASSESSMENT_BY_ID",
  GET_ASSESSMENT_BY_UUID = "GET_ASSESSMENT_BY_UUID",
  GET_ASSESSMENTS_BY_CLIENT = "GET_ASSESSMENTS_BY_CLIENT",
  UPDATE_ASSESSMENT = "UPDATE_ASSESSMENT",
  CREATE_ASSESSMENT = "CREATE_ASSESSMENT",
  DELETE_ASSESSMENT = "DELETE_ASSESSMENT",
  GET_SYMPTOMS = "GET_SYMPTOMS",
  GET_LABTEST = "GET_LABTEST",
  CREATE_ASSESSMENT_TEMPLATE = "CREATE_ASSESSMENT_TEMPLATE",
  UPDATE_ASSESSMENT_TEMPLATE = "UPDATE_ASSESSMENT_TEMPLATE",
  DELETE_ASSESSMENT_TEMPLATE = "DELETE_ASSESSMENT_TEMPLATE",
  GET_ASSESSMENT_TEMPLATE = "GET_ASSESSMENT_TEMPLATE"
}

export const getAssessments =
  ({
    searchValue,
    searchBy,
    page,
    pageSize
  }: {
    searchValue?: string;
    searchBy?: string;
    pageSize: number;
    page: number;
  }) =>
  async (dispatch: IThunkDispatch): Promise<void> => {
    await dispatch(
      actionCreatorAsync(Type.GET_ASSESSMENTS, async () =>
        api.getAssessments({ searchValue, searchBy, page, pageSize })
      )
    );
  };

export const getClientAssessments =
  (clientId: number) =>
  async (dispatch: IThunkDispatch): Promise<void> => {
    await dispatch(
      actionCreatorAsync(Type.GET_ASSESSMENTS_BY_CLIENT, async () =>
        api.getAssessments({
          searchBy: ASSESSMENT_SEARCH_BY.CLIENT,
          searchValue: String(clientId),
          limit
        })
      )
    );
  };

export const getAssessmentById =
  (assessmentId: number) =>
  async (dispatch: IThunkDispatch): Promise<void> => {
    await dispatch(
      actionCreatorAsync(Type.GET_ASSESSMENT_BY_ID, async () => api.getAssessmentById(assessmentId))
    );
  };

export const getAssessmentByUUId =
  (assessmentId: number) =>
  async (dispatch: IThunkDispatch): Promise<void> => {
    await dispatch(
      actionCreatorAsync(Type.GET_ASSESSMENT_BY_UUID, async () =>
        api.getAssessmentByUUID(assessmentId)
      )
    );
  };

export const updateAssessment =
  ({
    assessmentId,
    assessmentData
  }: {
    assessmentId: number;
    assessmentData: Assessment;
    isTemplateAssessment?: boolean;
  }) =>
  async (dispatch: IThunkDispatch): Promise<AsyncAction<Assessment>> =>
    dispatch(
      actionCreatorAsync(Type.UPDATE_ASSESSMENT, async () => {
        try {
          let imageInfo = {};
          if (
            assessmentData.assessmentImage &&
            assessmentData.assessmentImage.startsWith("data:image/")
          ) {
            imageInfo = await api.uploadAssessmentImage(assessmentData.assessmentImage);
          }
          let audioInfo = {};
          if (assessmentData.prescriptionAudioUrl?.startsWith("blob:")) {
            audioInfo = await api.uploadAssessmentAudio(assessmentData.prescriptionAudioUrl);
          }
          const assessment = await api.updateAssessment(assessmentId, {
            ...assessmentData,
            ...imageInfo,
            ...audioInfo
          });

          dispatch(push(`/assessment/assessments/?id=${assessment.id}`));
          return assessment;
        } catch (e: any) {
          dispatch(
            notificationAdd({
              id: new Date().getUTCMilliseconds(),
              variant: "error",
              message: e.message || updateErrorMessage(MODULE.ASSESSMENT),
              autoTimeout: true
            })
          );
          return null;
        }
      })
    );

export const createAssessment =
  (assessmentData: Assessment) =>
  async (dispatch: IThunkDispatch): Promise<AsyncAction<Assessment>> =>
    dispatch(
      actionCreatorAsync(Type.CREATE_ASSESSMENT, async () => {
        try {
          let imageInfo = {};
          if (assessmentData.assessmentImage?.startsWith("data:image/")) {
            imageInfo = await api.uploadAssessmentImage(assessmentData.assessmentImage);
          }
          let audioInfo = {};
          if (assessmentData.prescriptionAudioUrl?.startsWith("blob:")) {
            audioInfo = await api.uploadAssessmentAudio(assessmentData.prescriptionAudioUrl);
          }
          const assessment = await api.saveAssessment({
            ...assessmentData,
            ...imageInfo,
            ...audioInfo
          });

          dispatch(push(`/assessment/assessments/?id=${assessment.id}`));
          return assessment;
        } catch (e: any) {
          dispatch(
            notificationAdd({
              id: new Date().getUTCMilliseconds(),
              variant: "error",
              message: e.message || createErrorMessage(MODULE.ASSESSMENT),
              autoTimeout: true
            })
          );
          return null;
        }
      })
    );

export const deleteAssessment =
  ({
    assessmentId,
    isTemplateAssessment = true
  }: {
    assessmentId: number;
    isTemplateAssessment: boolean;
  }) =>
  async (dispatch: IThunkDispatch): Promise<void> => {
    dispatch(
      actionCreatorAsync(Type.DELETE_ASSESSMENT, async () => {
        if (isTemplateAssessment) {
          api.deleteTemplateAssessment(assessmentId);
        } else {
          api.deleteAssessmentTemplate(assessmentId);
        }
      })
    );
  };

export const getBatchSymptoms =
  (forceUpdate?: boolean) =>
  async (dispatch: IThunkDispatch): Promise<void> => {
    await dispatch(
      actionCreatorAsync(Type.GET_SYMPTOMS, async () => api.getBatchSymptoms(forceUpdate))
    );
  };

export const getBatchLabTests =
  (forceUpdate?: boolean) =>
  async (dispatch: IThunkDispatch): Promise<void> => {
    await dispatch(actionCreatorAsync(Type.GET_LABTEST, async () => api.getLabTest(forceUpdate)));
  };

// assessement templates actions
export const getAssessmentTemplates =
  () =>
  async (dispatch: IThunkDispatch): Promise<void> => {
    await dispatch(
      actionCreatorAsync(Type.GET_ASSESSMENT_TEMPLATE, async () => {
        try {
          const templates = await api.getAssessmentTemplates();
          return templates;
        } catch (e) {
          return undefined;
        }
      })
    );
  };

export const createAssessmentTemplate =
  (assessmentData: unknown) =>
  async (dispatch: IThunkDispatch): Promise<void> => {
    dispatch(
      actionCreatorAsync(Type.CREATE_ASSESSMENT_TEMPLATE, async () => {
        try {
          const { data, success, message } = await api.postAssessmentTemplate(assessmentData);
          if (success) {
            dispatch(
              notificationAdd({
                id: new Date().getUTCMilliseconds(),
                variant: "success",
                message: message || "Template assessment created successfully",
                autoTimeout: true
              })
            );
          }
          return data;
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } catch (err: any) {
          dispatch(
            notificationAdd({
              id: new Date().getUTCMilliseconds(),
              variant: "error",
              message: err?.message || "Error creating assessment template",
              autoTimeout: true
            })
          );

          return undefined;
        }
      })
    );
  };

export const updateAssessmentTemplate =
  ({ assessmentId, assessmentData }: { assessmentId: number; assessmentData: Assessment }) =>
  async (dispatch: IThunkDispatch): Promise<void> => {
    dispatch(
      actionCreatorAsync(Type.UPDATE_ASSESSMENT_TEMPLATE, async () => {
        try {
          const { data, success, message } = await api.patchAssessmentTemplate({
            assessmentData,
            assessmentId
          });
          if (success) {
            dispatch(
              notificationAdd({
                id: new Date().getUTCMilliseconds(),
                variant: "success",
                message: message || "Template assessment updated successfully",
                autoTimeout: true
              })
            );
          }
          return data;
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } catch (err: any) {
          dispatch(
            notificationAdd({
              id: new Date().getUTCMilliseconds(),
              variant: "error",
              message: err?.message || "Error uupdating template assessment",
              autoTimeout: true
            })
          );
          return undefined;
        }
      })
    );
  };

export const deleteAssessmentTemplate =
  (assessmentId: number) =>
  async (dispatch: IThunkDispatch): Promise<void> => {
    dispatch(
      // eslint-disable-next-line consistent-return
      actionCreatorAsync(Type.DELETE_ASSESSMENT_TEMPLATE, async () => {
        try {
          const { success, message } = await api.deleteAssessmentTemplate(assessmentId);
          if (success) {
            dispatch(
              notificationAdd({
                id: new Date().getUTCMilliseconds(),
                variant: "success",
                message: message || "Template assessment deleted successfully",
                autoTimeout: true
              })
            );
          }
          return assessmentId;
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } catch (err: any) {
          dispatch(
            notificationAdd({
              id: new Date().getUTCMilliseconds(),
              variant: "error",
              message: err?.message || "Error deleting template assessment",
              autoTimeout: true
            })
          );
        }
      })
    );
  };

export const getAssessmentTemplateImages =
  () =>
  async (dispatch: IThunkDispatch): Promise<void> => {
    try {
      const response = await api.getAssessmentTemplateImages({});
      const images = (response?.data ?? []) as AttachmentInterface[];
      dispatch(setAssessmentTemplateImagesInRC(images));
    } catch {
      // silently ignore — the component will just show empty
    }
  };

export const uploadAssessmentTemplateImage =
  (imageData: Blob, category: string) =>
  async (dispatch: IThunkDispatch): Promise<AttachmentInterface> => {
    try {
      const response = await api.uploadAssessmentTemplateImage(imageData, category);
      // response format: { success: true, message: '...', data: attachment }, see  the api

      const newImage = response?.data as AttachmentInterface;
      dispatch(addAssessmentTemplateImageToRC(newImage));
      dispatch(
        notificationAdd({
          id: new Date().getUTCMilliseconds(),
          variant: "success",
          message: "Image uploaded successfully",
          autoTimeout: true
        })
      );
      return newImage;
    } catch (e: any) {
      dispatch(
        notificationAdd({
          id: new Date().getUTCMilliseconds(),
          variant: "error",
          message: e.message || "Error uploading image",
          autoTimeout: true
        })
      );
      throw e;
    }
  };

export const deleteAssessmentTemplateImage =
  (id: number) =>
  async (dispatch: IThunkDispatch): Promise<void> => {
    try {
      await api.deleteAssessmentTemplateImage(id);
      dispatch(removeAssessmentTemplateImageFromRC(id));
      dispatch(
        notificationAdd({
          id: new Date().getUTCMilliseconds(),
          variant: "success",
          message: "Image deleted successfully",
          autoTimeout: true
        })
      );
    } catch (e: any) {
      dispatch(
        notificationAdd({
          id: new Date().getUTCMilliseconds(),
          variant: "error",
          message: e.message || "Error deleting image",
          autoTimeout: true
        })
      );
      throw e;
    }
  };
